/**
 * The two digits that move: one running clock, redrawn every second.
 *
 * ITS OWN INTERVAL, on purpose. The obvious place for a ticking value is the
 * provider, and that would re-render every screen in the CRM once a second for
 * the sake of a counter in the corner. Here the repaint stops at this span, and
 * there are never more than a handful of them on a page.
 *
 * Each tick RECOMPUTES from the start time instead of adding one to the last
 * figure. A background tab has its timers throttled to once a minute or worse,
 * so a counter that counted its own ticks would come back from lunch an hour
 * short - and it would be an hour short in the one place the operator looks to
 * decide whether the clock is still worth believing.
 */
import { useEffect, useState } from "react";
import { cn } from "@wa/ui";
import type { RunningTimer } from "../../lib/taskTimer";
import { elapsedSeconds, fmtElapsed } from "./elapsed";
import { useTimers } from "./TimerProvider";

export function ElapsedClock({ timer, className }: { timer: RunningTimer; className?: string }) {
  // The drift correction, or none when this is drawn outside the provider. Zero
  // is the honest fallback: it means "this browser's clock, uncorrected", which
  // is what any screen without the provider could have said anyway.
  const skewMs = useTimers()?.skewMs ?? 0;
  const [seconds, setSeconds] = useState(() => elapsedSeconds(timer.startedAt, skewMs));

  useEffect(() => {
    const read = () => setSeconds(elapsedSeconds(timer.startedAt, skewMs));
    read();
    const id = window.setInterval(read, 1000);
    return () => window.clearInterval(id);
  }, [timer.startedAt, skewMs]);

  return (
    // `crm-tnum` is tabular figures: without it the counter changes width twice
    // a second and everything beside it in the top bar shuffles.
    <span className={cn("crm-tnum", className)} aria-live="off">
      {fmtElapsed(seconds)}
    </span>
  );
}
